import {
  USER_APP_ROBOTS_PATH,
  USER_CONTRACT_CHECKOUT_PATH,
  USER_SUBSCRIPTION_PLANS_PATH,
} from '../User-app/paths';
import { defaultLandingPath, isHospitalRole } from './userDashboardPermissions';

/** Backend app routes (userLoginRedirect.js). */
export const APP_ROUTE_SUBSCRIPTION_PLANS = '/user/subscription/plans';
export const APP_ROUTE_CONTRACT_CHECKOUT = '/user/subscription/contract-checkout';
export const APP_ROUTE_ROBOTS = '/robots';

/** Values from getUserSubscriptionPaymentContext / enrichSubscriptionLoginExtras. */
export const PAYMENT_FLOW_PLAN_SELECTION = 'plan_selection';
export const PAYMENT_FLOW_CONTRACT_CHECKOUT = 'contract_checkout';
export const PAYMENT_FLOW_MANDATE_SETUP = 'mandate_setup';
export const PAYMENT_FLOW_FREE_MONTH_START = 'free_month_start';
export const PAYMENT_FLOW_SUBSCRIPTION_ACTIVE = 'subscription_active';
export const PAYMENT_FLOW_CONTRACT_PENDING_ADMIN = 'contract_pending_admin';

const CONTRACT_CHECKOUT_PAYMENT_FLOWS = new Set([
  PAYMENT_FLOW_CONTRACT_CHECKOUT,
  PAYMENT_FLOW_MANDATE_SETUP,
  PAYMENT_FLOW_FREE_MONTH_START,
]);

/** True when the user should complete payment on /user/subscription/contract-checkout. */
export function isContractCheckoutPaymentContext(ctx = {}) {
  if (CONTRACT_CHECKOUT_PAYMENT_FLOWS.has(ctx.paymentFlow)) return true;
  return String(ctx.redirectRoute ?? '').includes('contract-checkout');
}

function normalizePurchaseType(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function isSubscriptionPurchaseType(raw) {
  const norm = normalizePurchaseType(raw);
  return norm === 'subscriptionuser' || norm === 'subscription' || norm === 'subscriptioncustomer';
}

export function isUserPortalAppPath(pathname) {
  return (
    pathname.startsWith('/user-app') ||
    pathname.startsWith('/user/subscription')
  );
}

/** Maps backend `redirectRoute` to web paths. */
export function webPathFromAppRedirectRoute(redirectRoute) {
  const route = String(redirectRoute ?? '').trim();
  if (route === APP_ROUTE_CONTRACT_CHECKOUT) {
    return USER_CONTRACT_CHECKOUT_PATH;
  }
  if (
    route === APP_ROUTE_SUBSCRIPTION_PLANS ||
    route.startsWith('/user/subscription/plans')
  ) {
    return USER_SUBSCRIPTION_PLANS_PATH;
  }
  if (!route || route === APP_ROUTE_ROBOTS) {
    return USER_APP_ROBOTS_PATH;
  }
  return USER_APP_ROBOTS_PATH;
}

/**
 * Single source of truth from `paymentContext` (GET /user/subscription/status or login extras).
 * @param {object} [paymentContext]
 * @param {{ allowAccessWithoutPayment?: boolean }} [opts]
 */
export function resolvePathFromPaymentContext(paymentContext, opts = {}) {
  const allowBypass = Boolean(opts.allowAccessWithoutPayment);

  if (allowBypass) {
    return USER_APP_ROBOTS_PATH;
  }

  const ctx = paymentContext || {};

  if (ctx.canAccessRobots || ctx.paymentFlow === PAYMENT_FLOW_SUBSCRIPTION_ACTIVE) {
    return USER_APP_ROBOTS_PATH;
  }

  if (ctx.redirectRoute) {
    return webPathFromAppRedirectRoute(ctx.redirectRoute);
  }

  if (isContractCheckoutPaymentContext(ctx)) {
    return USER_CONTRACT_CHECKOUT_PATH;
  }

  if (ctx.paymentFlow === PAYMENT_FLOW_CONTRACT_PENDING_ADMIN) {
    return USER_SUBSCRIPTION_PLANS_PATH;
  }

  return USER_SUBSCRIPTION_PLANS_PATH;
}

/** Client post-login from `/login` (enrichSubscriptionLoginExtras). */
export function resolveClientPostLoginPath(data) {
  const accountType = String(data?.accountType ?? '').trim();
  const role = String(data?.role ?? '').trim();
  const isCustomer =
    accountType === 'customer' || isHospitalRole(role);
  if (!isCustomer) {
    return null;
  }
  if (data?.redirectRoute) {
    const route = String(data.redirectRoute).trim();
    if (route === APP_ROUTE_CONTRACT_CHECKOUT) {
      return USER_CONTRACT_CHECKOUT_PATH;
    }
    if (
      route === APP_ROUTE_SUBSCRIPTION_PLANS ||
      route.startsWith('/user/subscription/plans')
    ) {
      return USER_SUBSCRIPTION_PLANS_PATH;
    }
  }
  return defaultLandingPath(role);
}

/** Robot nav — prefer GET /user/subscription/status paymentContext. */
export function resolveRobotNavPathFromLoginExtras(extras = {}) {
  return resolvePathFromPaymentContext(
    {
      redirectRoute: extras.redirectRoute,
      paymentFlow: extras.paymentFlow,
      canAccessRobots: extras.canAccessRobots,
      needsPayment: extras.needsPayment,
    },
    { allowAccessWithoutPayment: extras.allowAccessWithoutPayment }
  );
}

/** Fallback when status API unavailable — uses `/client/subscription` plans. */
export function resolveRobotNavPathFromSubscription({
  purchaseType,
  plans,
  allowAccessWithoutPayment,
} = {}) {
  if (!isSubscriptionPurchaseType(purchaseType)) {
    return USER_APP_ROBOTS_PATH;
  }
  if (allowAccessWithoutPayment) {
    return USER_APP_ROBOTS_PATH;
  }
  const planList = Array.isArray(plans) ? plans : [];
  if (!planList.length) {
    return USER_SUBSCRIPTION_PLANS_PATH;
  }
  const last = planList[planList.length - 1];
  const status = String(last?.planpaymentStatus ?? '').trim().toLowerCase();
  if (status === 'trial' || status === 'active' || status === 'expired') {
    return USER_APP_ROBOTS_PATH;
  }
  return USER_SUBSCRIPTION_PLANS_PATH;
}
